import React from 'react';
import { Messages } from '../../../../../../gen-src/Localize';
import { LabelButtonContext, RenderLabelAssetProps, LabelsPopoverContext, groupBy } from '../LabelHelpers';
import { ViewLineageButton } from '../LabelLineageButton';
import { ExpandoSectionBreakClass, LabelGroupHeadingDiv, LabelLabelsGroupDiv } from '../Styles';
import { onThisAssetMessage, inheritedByThisAssetMessage } from '../shared/LabelAssetContent';
import { ExpandoWidgetWrapper, LabelDetailAuthorElement, LabelDetailDateElement, LabelDetailMessageElement, TextLinkWidgetLabelDetailAsset } from '../shared/LabelPopoverDetail';
import { HydratedWarningRenderProps, RenderParentedWarning, RenderUpstreamWarning, RenderWarning } from './WarningProps';

export const LabelDetailChipId = 'data-quality-detail-chip-id';

function groupWarningsByType(labels: RenderLabelAssetProps[]): RenderLabelAssetProps[][] {
  return Array.from(groupBy(labels, i => i.labels[0].type.toString() ?? '').values());
}

function mapWarningToLabelAssetProps(label: RenderParentedWarning | RenderUpstreamWarning): RenderLabelAssetProps {
  const { content } = label;
  const asset = ('inheritedBy' in label && label.inheritedBy) ? label.inheritedBy : content;
  return ({
    subjectArea: asset?.subjectArea ?? null,
    assetName: asset?.contentName ?? null,
    assetType: asset?.contentType ?? null,
    assetUrl: asset?.contentUrl ?? null,
    remoteType: asset?.remoteType ?? content?.remoteType ?? undefined,
    parentName: ('parent' in label ? label.parent?.contentName : undefined),
    parentUrl: ('parent' in label ? label.parent?.contentUrl : undefined),
    category: 'warning',
    labels: [label]
  });
}

/*
* produces a nested list of RenderLabelAssetProps which is grouped by labelType
*/
function sortWarningsBySeverityAndType(labels: readonly RenderWarning[]): RenderLabelAssetProps[][] {
  const severeLabels: RenderLabelAssetProps[] = labels.filter(i => i.severe === true).map(mapWarningToLabelAssetProps);
  const nonSevereLabels: RenderLabelAssetProps[] = labels.filter(i => i.severe === false).map(mapWarningToLabelAssetProps);

  return groupWarningsByType(severeLabels).concat(groupWarningsByType(nonSevereLabels));
}

function WarningExpandoSection(props: RenderLabelAssetProps & {
  readonly label: RenderWarning,
  readonly collapseDetails: boolean,
}): JSX.Element | null {
  const { linksDisabled } = React.useContext(LabelButtonContext);
  const { category, content: baseContent } = React.useContext(LabelsPopoverContext);
  const { assetName, assetType, assetUrl, remoteType, subjectArea, label } = props;
  return (
    <LabelLabelsGroupDiv>
      <TextLinkWidgetLabelDetailAsset
        assetName={assetName ?? baseContent?.contentName ?? null}
        assetType={assetType ?? baseContent?.contentType ?? null}
        assetUrl={linksDisabled ? null : (assetUrl ?? baseContent?.contentUrl ?? null)}
        remoteType={remoteType ?? baseContent?.remoteType}
        subjectArea={subjectArea ?? null}
      />
      {
        props.collapseDetails ? null :
          <>
            <LabelDetailAuthorElement {...label} category={category} />
            <LabelDetailDateElement {...label} />
            <LabelDetailMessageElement {...label} assetName={assetName ?? baseContent?.contentName ?? null} />
          </>
      }
    </LabelLabelsGroupDiv>
  )
}

function RenderWarnings(props: RenderLabelAssetProps & { readonly collapseDetails: boolean, readonly keyIndex?: string; }): JSX.Element {
  const { labels } = props;
  return (
    <React.Fragment key={props.keyIndex}>
      {
        labels.map((i: RenderWarning, index: React.Key | undefined) =>
          <React.Fragment key={index}>
            <WarningExpandoSection {...props} label={i} />
          </React.Fragment>
        )
      }
    </React.Fragment>
  )
}

function WarningAnchoredSection({ anchored, ...props }: Omit<HydratedWarningRenderProps, 'parented' | 'upstream'>): JSX.Element | null {
  if (!anchored || anchored.labels.length < 1) {
    return null;
  }

  const warnings: RenderLabelAssetProps[][] = sortWarningsBySeverityAndType(anchored?.labels);

  const { contentType } = props.content;
  return (
    <>
      <LabelGroupHeadingDiv>
        {onThisAssetMessage(contentType)}
      </LabelGroupHeadingDiv>
      {warnings.map((c, j) =>
        <ExpandoWidgetWrapper
          length={c.length}
          labelType={c[0].labels[0].type}
          isSevere={c[0].category === 'warning' ? c[0].labels[0].severe : false}
          overrides={{ collapse: 'disallow' }}
          key={j}
        >
          {c.map((x, i) =>
            <React.Fragment key={i}>
              <RenderWarnings {...x} collapseDetails={false} keyIndex={`${i}`} />
              <LabelLabelsGroupDiv>
                {(c.length > 1 && i < c.length - 1)?  <hr className={ExpandoSectionBreakClass}/> : null}
              </LabelLabelsGroupDiv>
            </React.Fragment>
          )}
        </ExpandoWidgetWrapper>)
      }
    </>
  )
}

function RenderParentedWarnings(props: { labels: RenderLabelAssetProps[], upstream: boolean } & { readonly keyIndex?: string; }): JSX.Element {
  const sortedLabels = props.labels.sort((a, b) => (a.assetName ?? '').localeCompare(b.assetName ?? ''));

  return (
    <>
      {
        sortedLabels.map((x, i) => {
          return (
            <React.Fragment key={i}>
              <RenderWarnings {...x} collapseDetails={true} keyIndex={`${i}`} />
            </React.Fragment>)
        })
      }
    </>
  )
}

function RenderNonParentedUpstreamSection(props: { labels: readonly RenderUpstreamWarning[], readonly message: string }): JSX.Element | null {
  if (!props.labels || props.labels.length < 1) {
    return null;
  }
  const warnings: RenderLabelAssetProps[][] = sortWarningsBySeverityAndType(props.labels);
  return (
    <>
      <LabelGroupHeadingDiv>
        {props.message}
      </LabelGroupHeadingDiv>
      {warnings.map((c, j) =>
        <ExpandoWidgetWrapper
          length={c.length}
          labelType={c[0].labels[0].type}
          isSevere={c[0].category === 'warning' ? c[0].labels[0].severe : false}
          key={j}
        >
          {c.map((x, i) =>
            <React.Fragment key={i}>
              <RenderWarnings {...x} collapseDetails={false} keyIndex={`${i}`} />
              <LabelLabelsGroupDiv>
                {(c.length > 1 && i < c.length - 1)?  <hr className={ExpandoSectionBreakClass}/> : null}
              </LabelLabelsGroupDiv>
            </React.Fragment>
          )}
        </ExpandoWidgetWrapper>
      )}
    </>
  );
}

function RenderInheritedAndOrParentedSection(props: { inheritedColumnWarnings: readonly RenderUpstreamWarning[], directParentedWarnings: readonly RenderParentedWarning[] }): JSX.Element | null {
  const inheritedAndParentedWarnings = props.inheritedColumnWarnings.concat(props.directParentedWarnings);
  if (inheritedAndParentedWarnings.length < 1) {
    return null;
  }

  const asset = inheritedAndParentedWarnings[0].inheritedBy ?? inheritedAndParentedWarnings[0].content;
  const warnings: RenderLabelAssetProps[][] = sortWarningsBySeverityAndType(inheritedAndParentedWarnings);

  return (
    <>
      <LabelGroupHeadingDiv>
        {props.inheritedColumnWarnings.length > 0 ?
          <>
            <span>{inheritedByThisAssetMessage(asset.contentType ?? asset.subjectArea ?? null, inheritedAndParentedWarnings.length)}</span>
            <ViewLineageButton contentType={asset.contentType ?? asset.subjectArea ?? null} />
          </>
          : (props.directParentedWarnings.length > 0 ? Messages.Label_direct_columns({ MESSAGE_COUNT: props.directParentedWarnings.length }) : null)}
      </LabelGroupHeadingDiv>
      <>
        {
          warnings.map((c, i) => {
            return (
              <ExpandoWidgetWrapper
                length={c.length}
                labelType={c[0].labels[0].type}
                isSevere={c[0].category === 'warning' ? c[0].labels[0].severe : false}
                key={i}
              >
                <RenderParentedWarnings labels={c} upstream={true} key={i} keyIndex={`${i}`} />
              </ExpandoWidgetWrapper>
            )
          })
        }
      </>
    </>
  )
}

function WarningUpstreamAndParentedSection({ upstream, parented, ...props }: Omit<HydratedWarningRenderProps, 'anchored'>): JSX.Element | null {
  const inheritedColumnWarnings = upstream?.labels.filter(i => i.inheritedBy) ?? [];
  const directInheritedWarnings = upstream?.labels.filter(i => !(i.inheritedBy)) ?? [];
  const directParentedWarnings = parented ? parented.labels : [];
  return (
    <>
      {(directInheritedWarnings.length > 0) && <div style={{ height: '10px' }}></div>}
      <RenderNonParentedUpstreamSection labels={directInheritedWarnings} message={inheritedByThisAssetMessage(props.content.subjectArea ?? null, directInheritedWarnings.length)} />
      {(inheritedColumnWarnings.length > 0 || directParentedWarnings.length > 0) && <div style={{ height: '10px' }}></div>}
      <RenderInheritedAndOrParentedSection inheritedColumnWarnings={inheritedColumnWarnings} directParentedWarnings={directParentedWarnings} />
    </>
  )
}

export function WarningPopover(props: HydratedWarningRenderProps): JSX.Element {
  const anchoredSection = WarningAnchoredSection(props);
  const upstreamAndParentedSection = WarningUpstreamAndParentedSection(props);

  if (!anchoredSection && !upstreamAndParentedSection) {
    return (<></>);
  }

  return (
    <>
      {anchoredSection && anchoredSection}
      {upstreamAndParentedSection && upstreamAndParentedSection}
    </>
  );
}
